import { useState } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { apiFetch } from '../../lib/api';
import { VALID_PRICES, FEATURES, PAYMENT_TERMS } from '../../data/conjointData';
import { formatPrice, formatPercent, formatNumber, formatCurrencyCompact, pnlColorClass } from '../../lib/formatters';
import { resolvePackageCosts } from '../../lib/calculations';
import { Select } from '../common/Select';
import { Toggle } from '../common/Toggle';
import { AnimatedNumber } from '../common/AnimatedNumber';
import { InfoButton } from '../common/Tooltip';
import { TrendingUp, Users, Bookmark, Check, ArrowUpRight, ChevronDown, ChevronUp, RotateCcw, Settings2, X } from 'lucide-react';
import { MIN_PACKAGES } from '../../data/conjointData';
import type { PackageConfig, PackageCostOverrides, ContributionResult, PaymentTermKey } from '../../types';

interface PackageCardProps {
 index: number;
 config: PackageConfig;
 contribution: ContributionResult;
 demandMode: 'ceiling' | 'split';
 isRecommended?: boolean;
}

const COST_OVERRIDE_FIELDS: { key: keyof PackageCostOverrides; label: string; format: 'dollar' | 'percent'; step: number; min: number; max: number }[] = [
 { key: 's1CostPerUnit', label: 'S1 Cost / Kit', format: 'dollar', step: 1, min: 5, max: 200 },
 { key: 's2CloseRate', label: 'S2 Close Rate', format: 'percent', step: 0.01, min: 0.05, max: 0.60 },
 { key: 'totalDeductionRate', label: 'Deduction Rate', format: 'percent', step: 0.005, min: 0.05, max: 0.50 },
 { key: 's2RepCost', label: 'S2 Rep Cost', format: 'dollar', step: 5, min: 0, max: 500 },
 { key: 'kitReturnRate', label: 'Kit Return Rate', format: 'percent', step: 0.01, min: 0.50, max: 1.0 },
 { key: 'consultRate', label: 'Consult Rate', format: 'percent', step: 0.01, min: 0.50, max: 1.0 },
];

export function PackageCard({ index, config, contribution, demandMode, isRecommended }: PackageCardProps) {
 const setPackagePrice = useSimulatorStore(s => s.setPackagePrice);
 const setPackagePayment = useSimulatorStore(s => s.setPackagePayment);
 const toggleFeature = useSimulatorStore(s => s.toggleFeature);
 const setAllFeatures = useSimulatorStore(s => s.setAllFeaturesForPackage);
 const setCostOverride = useSimulatorStore(s => s.setPackageCostOverride);
 const setS1Name = useSimulatorStore(s => s.setPackageS1Name);
 const removePackage = useSimulatorStore(s => s.removePackage);
 const packageCount = useSimulatorStore(s => s.packages.length);
 const assumptions = useSimulatorStore(s => s.assumptions);

 const [saving, setSaving] = useState(false);
 const [saved, setSaved] = useState(false);
 const [saveError, setSaveError] = useState<string | null>(null);
 const [saveName, setSaveName] = useState('');
 const [showSaveForm, setShowSaveForm] = useState(false);
 const [showCostOverrides, setShowCostOverrides] = useState(false);

 const resolved = resolvePackageCosts(config, assumptions);
 const overrides = config.costOverrides || {};
 const hasOverrides = Object.keys(overrides).length > 0;

 const handleSave = async () => {
 const name = saveName.trim() || config.name;
 setSaving(true);
 try {
 await apiFetch('/api/packages', {
 method: 'POST',
 body: JSON.stringify({
 name,
 price: config.price,
 paymentTerm: config.paymentTerm,
 enabledFeatures: config.enabledFeatures,
 assumptions,
 }),
 });
 setSaved(true);
 setShowSaveForm(false);
 setTimeout(() => setSaved(false), 4000);
 } catch {
 setSaveError('Failed to save — check server connection');
 setTimeout(() => setSaveError(null), 5000);
 }
 setSaving(false);
 };

 const margin = demandMode === 'ceiling' ? contribution.contributionMarginCeiling : contribution.contributionMarginSplit;
 const s2Units = demandMode === 'ceiling' ? contribution.s2UnitsCeiling : contribution.s2UnitsSplit;
 const netRevenue = demandMode === 'ceiling' ? contribution.netRevenueCeiling : contribution.netRevenueSplit;
 const isPositiveContrib = margin >= 0;
 const borderColor = isPositiveContrib ? 'border-metric-positive/30' : 'border-metric-negative/30';

 return (
 <div className={`card ${borderColor} flex flex-col`}>
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-2">
 <h3 className="text-lg font-bold text-primary">{config.name}</h3>
 {isRecommended && (
 <span className="text-micro font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-chart-teal/15 text-chart-teal ring-1 ring-chart-teal/30">Best</span>
 )}
 </div>
 <div className="flex items-center gap-2">
 {saved ? (
 <span className="flex items-center gap-1 text-micro text-chart-teal font-medium">
 <Check size={12} /> Saved
 </span>
 ) : (
 <button
 onClick={() => setShowSaveForm(!showSaveForm)}
 className="flex items-center gap-1 text-micro text-muted hover:text-chart-teal transition-colors"
 title="Save for marketing strategy"
 >
 <Bookmark size={12} />
 <span className="hidden sm:inline">Save</span>
 </button>
 )}
 <div className={`flex items-center gap-1 ${isPositiveContrib ? 'text-metric-positive' : 'text-metric-negative'}`}>
 <ArrowUpRight size={14} className={isPositiveContrib ? '' : 'rotate-90'} />
 <span className="text-micro font-medium">{isPositiveContrib ? 'Contributes' : 'Drags'}</span>
 </div>
 {packageCount > MIN_PACKAGES && (
 <button
 onClick={() => removePackage(index)}
 className="ml-1 p-1 rounded hover:bg-metric-negative/10 text-muted hover:text-metric-negative transition-colors"
 title="Remove package"
 >
 <X size={14} />
 </button>
 )}
 </div>
 </div>

 {showSaveForm && (
 <div className="mb-4 p-3 rounded-lg bg-chart-teal/5 border border-chart-teal/20">
 <label className="text-xs text-muted uppercase tracking-wider font-semibold mb-1 block">Package Name</label>
 <div className="flex gap-2">
 <input
 type="text"
 value={saveName}
 onChange={e => setSaveName(e.target.value)}
 placeholder={config.name}
 className="flex-1 px-2 py-1.5 text-xs rounded border border-border text-primary"
              style={{ backgroundColor: 'var(--color-input-bg)' }}
 />
 <button
 onClick={handleSave}
 disabled={saving}
 className="px-3 py-1.5 text-xs font-medium rounded bg-chart-teal text-white hover:bg-teal-600 disabled:opacity-50 transition-colors"
 >
 {saving ? '...' : 'Save'}
 </button>
 </div>
 <p className="text-xs text-muted mt-1.5">Saves this configuration for the Marketing Strategy tab.</p>
 </div>
 )}

 {saveError && (
 <div className="mb-3 px-3 py-2 rounded-lg bg-metric-negative/10 border border-metric-negative/30 text-xs text-metric-negative">
 {saveError}
 </div>
 )}

 {/* S1 → S2 Funnel */}
 <div className="mb-3 px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
 <div className="flex items-center gap-1 mb-1">
 <span className="text-xs text-muted uppercase tracking-wider font-semibold">S1 → S2 Funnel</span>
 <InfoButton helpKey="funnelModel" size={10} />
 </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted">S1:</span>
          <InfoButton helpKey="s1OfferName" size={9} />
          <input
            type="text"
            value={config.s1OfferName || ''}
            onChange={e => setS1Name(index, e.target.value)}
            placeholder="S1 Offer Name"
            className="flex-1 px-1.5 py-0.5 text-xs rounded border border-border/60 bg-transparent text-primary font-medium min-w-0"
          />
 <span className="text-muted shrink-0">({formatPrice(resolved.s1CostPerUnit)})</span>
 <span className="text-muted shrink-0">→</span>
 <span className="text-secondary font-medium shrink-0">S2: {formatPrice(config.price)}</span>
 </div>
 </div>

 <div className="mb-3">
 <div className="flex items-center gap-1 mb-1">
 <label className="text-xs text-secondary">S2 Price</label>
 <InfoButton helpKey="priceSelect" size={11} />
 </div>
 <Select
 value={config.price}
 options={VALID_PRICES.map(p => ({ value: p, label: formatPrice(p) }))}
 onChange={v => setPackagePrice(index, parseInt(v))}
 />
 </div>

 <div className="mb-4">
 <div className="flex items-center gap-1 mb-1">
 <label className="text-xs text-secondary">Payment Terms</label>
 <InfoButton helpKey="paymentTerms" size={11} />
 </div>
 <Select
 value={config.paymentTerm}
 options={PAYMENT_TERMS.map(pt => ({ value: pt.key, label: pt.label }))}
 onChange={v => setPackagePayment(index, v as PaymentTermKey)}
 />
 <p className="text-xs text-muted mt-1">Minimal demand impact (all within 1.7 percentage points in conjoint)</p>
 </div>

 <div className="mb-4 flex-1">
 <div className="flex items-center justify-between mb-2">
 <div className="flex items-center gap-1">
 <label className="text-xs text-secondary uppercase tracking-wider">Features</label>
 <InfoButton helpKey="featureToggles" size={11} />
 </div>
 <div className="flex gap-2">
 <button onClick={() => setAllFeatures(index, true)} className="text-xs text-muted hover:text-chart-teal transition-colors">All ON</button>
 <button onClick={() => setAllFeatures(index, false)} className="text-xs text-muted hover:text-metric-negative transition-colors">All OFF</button>
 </div>
 </div>
 <div className="space-y-1.5">
 {FEATURES.map(f => {
 const cost = assumptions.featureCosts?.[f.name] || 0;
 return (
 <div key={f.name} className="flex items-center justify-between" title={f.description ?? undefined}>
 <Toggle enabled={config.enabledFeatures.includes(f.name)} onChange={() => toggleFeature(index, f.name)} label={f.name} size="sm" />
 <span className="text-micro font-mono text-muted shrink-0 ml-1">+{formatPrice(cost)}</span>
 </div>
 );
 })}
 </div>
 </div>

 {/* Cost Overrides accordion */}
 <div className="mb-4 border-t border-border/50 pt-3">
 <button
 onClick={() => setShowCostOverrides(!showCostOverrides)}
 className="flex items-center justify-between w-full text-left group"
 >
 <div className="flex items-center gap-1.5">
            <Settings2 size={12} className={hasOverrides ? 'text-chart-amber' : 'text-muted'} />
            <span className="text-xs text-secondary uppercase tracking-wider font-semibold">Cost Parameters</span>
            <InfoButton helpKey="costOverrides" size={10} />
 {hasOverrides && (
 <span className="text-micro px-1.5 py-0.5 rounded-full bg-chart-amber/15 text-chart-amber font-medium">
 {Object.keys(overrides).length} custom
 </span>
 )}
 </div>
 {showCostOverrides ? <ChevronUp size={12} className="text-muted" /> : <ChevronDown size={12} className="text-muted" />}
 </button>

 {showCostOverrides && (
 <div className="mt-2 space-y-1.5">
 {COST_OVERRIDE_FIELDS.map(field => {
 const globalVal = assumptions[field.key as keyof typeof assumptions] as number;
 const overrideVal = overrides[field.key];
 const isOverridden = overrideVal !== undefined;
 const displayVal = resolved[field.key];

 return (
 <div key={field.key} className="flex items-center gap-2">
 <span className={`text-xs w-24 shrink-0 ${isOverridden ? 'text-chart-amber font-medium' : 'text-muted'}`}>
 {field.label}
 </span>
 <input
 type="number"
 value={field.format === 'percent' ? +(displayVal * 100).toFixed(1) : +displayVal.toFixed(2)}
 step={field.format === 'percent' ? +(field.step * 100).toFixed(1) : field.step}
 min={field.format === 'percent' ? +(field.min * 100).toFixed(0) : field.min}
 max={field.format === 'percent' ? +(field.max * 100).toFixed(0) : field.max}
 onChange={e => {
 const raw = parseFloat(e.target.value);
 if (isNaN(raw)) return;
 const val = field.format === 'percent' ? raw / 100 : raw;
 setCostOverride(index, field.key, val);
 }}
 className={`w-20 px-1.5 py-1 text-detail font-mono rounded border text-right ${
 isOverridden
 ? 'border-chart-amber/40 bg-chart-amber/5 text-primary'
 : 'border-border text-muted'
 }`}
 style={{ backgroundColor: isOverridden ? undefined : 'var(--color-surface)' }}
 />
 <span className="text-xs text-muted w-4">{field.format === 'percent' ? '%' : '$'}</span>
 {isOverridden && (
 <button
 onClick={() => setCostOverride(index, field.key, undefined)}
 className="text-muted hover:text-chart-amber transition-colors"
 title={`Reset to global (${field.format === 'percent' ? (globalVal * 100).toFixed(1) + '%' : '$' + globalVal.toFixed(0)})`}
 >
 <RotateCcw size={10} />
 </button>
 )}
 </div>
 );
 })}
 <p className="text-xs text-muted pt-1">
 Amber values override the global assumptions for this package only.
 </p>
 </div>
 )}
 </div>

 <div className="border-t border-border pt-4 space-y-3">
 <h4 className="text-xs text-muted uppercase tracking-wider font-semibold">Demand</h4>

 <div className="mb-1">
 <div className="flex items-center justify-between mb-1">
 <div className="flex items-center gap-1.5">
 <TrendingUp size={13} className="text-chart-teal" />
 <span className="text-xs text-secondary">Purchase Probability</span>
 <InfoButton helpKey="adjChoiceRate" size={11} />
 </div>
 <AnimatedNumber value={contribution.adjustedChoiceRate} formatter={v => formatPercent(v)} className="text-2xl font-bold font-mono text-primary" />
 </div>
 <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
 <div className="h-full rounded-full bg-chart-teal transition-all duration-500" style={{ width: `${Math.min(contribution.adjustedChoiceRate * 100 * 2, 100)}%` }} />
 </div>
 </div>

 <ResultRow label="Required Close Rate" helpKey="impliedCloseRate">
 <div className="text-right">
 <AnimatedNumber value={contribution.impliedCloseRate} formatter={v => formatPercent(v)} className={closeRateColor(contribution.impliedCloseRate)} />
 <div className="text-xs text-muted">vs. 30% proven</div>
 </div>
 </ResultRow>
 <ResultRow label="S2 Units (scenario)" helpKey="s2Units" icon={<Users size={13} className="text-muted" />}>
 <AnimatedNumber value={s2Units} formatter={v => formatNumber(v)} className="text-primary" />
 </ResultRow>

 <div className="border-t border-border/50 pt-3 mt-3 space-y-2">
 <h4 className="text-xs text-muted uppercase tracking-wider">Contribution to Portfolio</h4>
 <ResultRow label="Contribution Margin" helpKey="contributionMargin">
 <AnimatedNumber value={margin} formatter={v => formatCurrencyCompact(v)} className={`text-sm font-semibold ${pnlColorClass(margin)}`} />
 </ResultRow>
 <ResultRow label="Net Revenue" helpKey="netRevenue">
 <AnimatedNumber value={netRevenue} formatter={v => formatCurrencyCompact(v)} className="text-secondary" />
 </ResultRow>
 <ResultRow label="Net Income / Unit" helpKey="netIncomePerUnit">
 <NetIncomePerUnit contribution={contribution} config={config} />
 </ResultRow>
 </div>
 </div>
 </div>
 );
}

function NetIncomePerUnit({ contribution, config }: { contribution: ContributionResult; config: PackageConfig }) {
 const assumptions = useSimulatorStore(s => s.assumptions);
 const costs = resolvePackageCosts(config, assumptions);
 const featureCost = config.enabledFeatures.reduce((sum, f) => sum + (assumptions.featureCosts?.[f] || 0), 0);
 const s1Acq = costs.s2CloseRate > 0 ? Math.round(costs.s1CostPerUnit / costs.s2CloseRate) : 0;
 const netPerUnit = Math.round(config.price * (1 - costs.totalDeductionRate));
 const netIncome = Math.round(contribution.netIncomePerUnit);
 const tooltip = `$${config.price} × ${((1 - costs.totalDeductionRate) * 100).toFixed(1)}% = $${netPerUnit} net, minus S1 acq $${s1Acq} ($${costs.s1CostPerUnit.toFixed(0)}/${(costs.s2CloseRate * 100).toFixed(0)}% close), S2 rep $${costs.s2RepCost}, features $${featureCost} = $${netIncome}/unit`;
 return (
 <span className={`font-mono text-sm ${netIncome >= 0 ? 'text-primary' : 'text-metric-negative'}`} title={tooltip}>
 {formatPrice(netIncome)}
 </span>
 );
}

function closeRateColor(rate: number): string {
 if (rate <= 0.30) return 'text-metric-positive font-semibold';
 if (rate <= 0.35) return 'text-chart-amber font-semibold';
 return 'text-metric-negative font-semibold';
}

function ResultRow({ label, helpKey, icon, children }: { label: string; helpKey?: string; icon?: React.ReactNode; children: React.ReactNode }) {
 return (
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-1.5">
 {icon}
 <span className="text-xs text-secondary">{label}</span>
 {helpKey && <InfoButton helpKey={helpKey} size={11} />}
 </div>
 <div className="font-mono text-sm">{children}</div>
 </div>
 );
}
